/**
 * ReviewsList — anzeigbare Review-Liste mit Ø + Textausschnitten.
 * Kann sowohl LISTING-scoped als auch SELLER-scoped Reviews rendern.
 *
 * Anonymisierung erfolgt SERVERSEITIG (siehe `_anonymize_reviewer` in
 * `backend/routes/review_routes.py`) — Frontend zeigt einfach `reviewer_display`.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Star } from "lucide-react";
import api from "@/lib/api";

const AREA_MAP = { STX: "STILLTOUCHEDX", MP: "MARKETPLACE", SOCIAL: "SOCIAL" };

export default function ReviewsList({
  scope = "listing",   // "listing" | "seller"
  scopeId,             // listing_id or seller_id
  area = "MP",         // STX | MP | SOCIAL
  limit = 5,
  showAll = false,
  showAvgHeader = true,
  emptyText,
}) {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState({ reviews: [], avg_rating: 0, review_count: 0 });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(showAll);
  const scopeArea = AREA_MAP[area] || area;

  useEffect(() => {
    if (!scopeId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const endpoint = scope === "seller"
          ? `/reviews/for-seller/${scopeId}?area=${encodeURIComponent(scopeArea)}&limit=${expanded ? 50 : limit}`
          : `/reviews/for-listing/${scopeId}?area=${encodeURIComponent(scopeArea)}&limit=${expanded ? 50 : limit}`;
        const { data: d } = await api.get(endpoint);
        if (!cancelled) setData(d);
      } catch { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [scope, scopeId, scopeArea, limit, expanded]);

  if (loading) return <div className="text-xs text-[#999] py-4">{t("reviews.loading")}</div>;

  const empty = emptyText ?? t("reviews.empty_generic");

  return (
    <div data-testid={`reviews-list-${scope}-${scopeId}`} className="space-y-3">
      {showAvgHeader && (
        <div className="flex items-center gap-3">
          {data.review_count > 0 ? (
            <>
              <div className="inline-flex items-center gap-1">
                <Star size={18} className="fill-[#f5b400] text-[#f5b400]" />
                <span className="text-lg font-bold text-[#0A0A0A] dark:text-white [.stx-theme:not(.stx-light)_&]:text-stx-text" data-testid="reviews-avg-rating">
                  {(data.avg_rating || 0).toFixed(1)}
                </span>
                <span className="text-xs text-[#666] [.stx-theme:not(.stx-light)_&]:text-stx-text-muted">/ 5</span>
              </div>
              <span className="text-xs text-[#666] [.stx-theme:not(.stx-light)_&]:text-stx-text-muted" data-testid="reviews-count">
                {data.review_count === 1
                  ? t("reviews.count_one")
                  : t("reviews.count_other", { n: data.review_count })}
              </span>
            </>
          ) : (
            <span className="text-xs text-[#999]">{empty}</span>
          )}
        </div>
      )}

      {data.reviews.length > 0 && (
        <ul className="space-y-2" data-testid="reviews-list">
          {data.reviews.map((r) => (
            <li key={r.review_id}
                className="border border-[#EEE] rounded-lg p-3 bg-white [.stx-theme:not(.stx-light)_&]:bg-stx-panel [.stx-theme:not(.stx-light)_&]:border-stx-border"
                data-testid={`review-item-${r.review_id}`}>
              <div className="flex items-center gap-2 mb-1">
                <div className="inline-flex">
                  {[1,2,3,4,5].map(i => (
                    <Star key={i} size={12}
                      className={i <= (r.rating || 0) ? "fill-[#f5b400] text-[#f5b400]" : "text-[#DDD]"} />
                  ))}
                </div>
                <span className="text-xs font-medium text-[#0A0A0A] [.stx-theme:not(.stx-light)_&]:text-stx-text" data-testid={`review-reviewer-${r.review_id}`}>
                  {r.reviewer_display || "User"}
                </span>
                <span className="text-[10px] text-[#999] [.stx-theme:not(.stx-light)_&]:text-stx-text-dim ml-auto">{fmtDate(r.created_at, i18n.language)}</span>
              </div>
              {r.text && (
                <p className="text-sm text-[#333] [.stx-theme:not(.stx-light)_&]:text-stx-text-muted leading-snug" data-testid={`review-text-${r.review_id}`}>
                  {r.text}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}

      {!expanded && data.review_count > data.reviews.length && (
        <button data-testid="reviews-show-all" onClick={() => setExpanded(true)}
          className="text-xs text-[#d97a63] hover:underline">
          {t("reviews.view_all", { n: data.review_count })}
        </button>
      )}
    </div>
  );
}

function fmtDate(iso, lang) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(lang, { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return ""; }
}
